import { scopedThreadKey } from "@afar-research/client-runtime/environment";
import { squashAtomCommandFailure } from "@afar-research/client-runtime/state/runtime";
import { providerGoalActionError, providerGoalRequestState } from "@afar-research/afar";
import type { ProviderGoalAction, ScopedThreadRef } from "@afar-research/contracts";
import { useState } from "react";
import { providerConversationOperationSupported } from "../providerInstances";
import { useThreadProvider } from "./useThreadProvider";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";

export function useProviderGoal(ref: ScopedThreadRef | null) {
  const { thread, config, instanceId, driver } = useThreadProvider(ref);
  const control = useAtomCommand(threadEnvironment.controlGoal);
  const [requestingFor, setRequestingFor] = useState<string | null>(null);
  const key = ref ? scopedThreadKey(ref) : null;
  const pending = key !== null && requestingFor === key;
  const supported =
    instanceId !== undefined &&
    ref !== null &&
    providerConversationOperationSupported(config?.providers ?? [], instanceId, "threadGoal");
  const request = providerGoalRequestState(thread?.activities ?? []);
  return {
    driver,
    supported,
    pending: pending || request.pending,
    error: request.error,
    thread,
    send: async (input: ProviderGoalAction) => {
      if (!supported || !thread || !ref)
        throw new Error("Native Goal support is unavailable for this conversation.");
      if (pending || request.pending)
        throw new Error("Wait for the current Goal action to finish.");
      const problem = providerGoalActionError(thread.providerGoal, input);
      if (problem) throw new Error(problem);
      setRequestingFor(key);
      try {
        const result = await control({
          environmentId: ref.environmentId,
          input: { ...input, threadId: ref.threadId, expectedRevision: thread.updatedAt },
        });
        if (result._tag === "Failure") throw squashAtomCommandFailure(result);
      } finally {
        setRequestingFor((current) => (current === key ? null : current));
      }
    },
  };
}
